import { supabase } from '../supabase';
import { writeAuditEvent, AUDIT_ENTITY_TYPES, AUDIT_EVENT_TYPES } from './auditEvents';
import { removeStoredDocument } from '../utils/pdf/storedDocumentPdf';
// getEstimates / getArchivedEstimates moved to ./estimateReads.js to keep this
// file under the 500-line cap. Re-exported immediately below so existing
// `import { getEstimates } from './estimateService'` call sites keep working
// without import-path churn. Pure move — no behavior change.
import { getEstimates, getArchivedEstimates } from './estimateReads';

export { getEstimates, getArchivedEstimates };

/**
 * The event_type emitted when `archived_at` is CLEARED. Deliberately the
 * lowercase literal and NOT `AUDIT_EVENT_TYPES.ESTIMATE_RESTORED`: that constant
 * is the legacy UPPERCASE spelling the old client-side duplicate wrote. Restore
 * used to emit BOTH — one row here and one from
 * useEstimateArchiveAndRestore — which is the "duplicate entry" bug. The client
 * write is gone and this is now the only writer, so `ESTIMATE_RESTORED` has zero
 * emitters. Keep its display-meta / eventCatalog / AUDIT_EVENT_TYPES entries
 * anyway — historical rows still render under it.
 *
 * DURABILITY, stated plainly so nobody reads "the service is the single writer"
 * as "the server writes it": this survivor is still a BROWSER-SIDE
 * fire-and-forget audit write in updateEstimate below — the same durability
 * class as the hook write that was removed. The fix took restore from TWO client
 * rows to ONE client row; it did not move the write server-side. Unlike
 * `extendEstimateExpiration` (whose RPC self-audits inside the transaction), an
 * `estimates` UPDATE has no server-side audit trigger, so an iOS-PWA suspend
 * between the UPDATE and the audit POST can still drop the row. Moving it into a
 * DEFINER RPC is the real durability fix and is NOT what happened here.
 *
 * Read-side only (the payload branch below + tests). The emitting site in
 * pickEstimateEditEventType deliberately repeats the literal — see the comment
 * there before "deduplicating" the two.
 */
export const ESTIMATE_RESTORED_EVENT_TYPE = 'restored';

// Map an updateEstimate input payload to the most-specific audit event type.
// Status transitions take precedence over archived_at toggles, which take
// precedence over generic field edits.
function pickEstimateEditEventType(estimateData = {}) {
  const s = estimateData.status;
  if (s === 'sent') return AUDIT_EVENT_TYPES.ESTIMATE_SENT;
  if (s === 'accepted') return AUDIT_EVENT_TYPES.ESTIMATE_ACCEPTED;
  if (s === 'declined') return AUDIT_EVENT_TYPES.ESTIMATE_DECLINED;
  if (s === 'invoiced' || s === 'partially_invoiced') return AUDIT_EVENT_TYPES.ESTIMATE_CONVERTED;
  // archived_at present in payload — handle restore vs archive.
  // 'restored' MUST stay an inline string literal here, NOT the
  // ESTIMATE_RESTORED_EVENT_TYPE constant: auditEventCoverage.derived.test.js
  // statically resolves this function's `return`s, and resolveJsValue only
  // understands literals, `CONST.MEMBER`, and ternaries — a bare identifier
  // makes it bail on EVERY branch, so both of this file's `eventType` call
  // sites go unresolved and the coverage guard fails. The constant below is
  // pinned to this value by estimateService.restore.test.js.
  if (Object.prototype.hasOwnProperty.call(estimateData, 'archived_at')) {
    return estimateData.archived_at ? AUDIT_EVENT_TYPES.ESTIMATE_ARCHIVED : 'restored';
  }
  return AUDIT_EVENT_TYPES.ESTIMATE_EDITED;
}

/**
 * Audit payload for an updateEstimate / updateEstimateWithItems write.
 * Light by design — a full diff on every edit would be noisy. Status
 * transitions are captured explicitly; generic edits just list the changed
 * field NAMES so admins can see the shape of the change.
 *
 * The identifying context an admin actually reads in the Activity Log
 * (estimate_number, customer_name, total, estimate_status) is added
 * server-side by the enrich_audit_event_payload BEFORE INSERT trigger, NOT
 * here — and caller keys WIN that merge, so never send a client-side
 * `estimate_status`/`customer_name`: it would override the authoritative
 * server value with whatever local state happened to hold.
 *
 * @param {string} eventType   from pickEstimateEditEventType
 * @param {object} estimateData the columns being written
 * @param {{ statusAtRestore?: string|null }} [auditContext] updateEstimate's
 *   `options` bag. Audit-only extras — never merged into the DB update. Only
 *   `statusAtRestore` is read here; `suppressAudit` is handled by the caller
 *   (this function is never reached when it is set).
 */
function buildEstimateEditAuditPayload(eventType, estimateData, auditContext = {}) {
  if (eventType === AUDIT_EVENT_TYPES.ESTIMATE_EDITED) {
    return { changed_fields: Object.keys(estimateData || {}) };
  }
  // ESTIMATE_DECLINED: structured reason so reports can read from audit_events
  // without joining back to estimates.
  if (eventType === AUDIT_EVENT_TYPES.ESTIMATE_DECLINED) {
    return {
      to_status: 'declined',
      decline_reason: estimateData?.decline_reason ?? null,
      decline_reason_note: estimateData?.decline_reason_note ?? null,
    };
  }
  if (eventType === ESTIMATE_RESTORED_EVENT_TYPE) {
    // A restore writes ONLY { archived_at: null }, so the generic `to_status`
    // branch below would record a permanent `{ to_status: null }` — a row that
    // says nothing. Carry the estimate's status at restore time instead, under
    // the same `status` key (and same value: `status || 'draft'`) the removed
    // client-side duplicate used, so old rows and new rows read identically.
    return { status: auditContext.statusAtRestore || 'draft' };
  }
  return { to_status: estimateData?.status || null };
}

export const getNextEstimateNumber = async (tenantId) => {
  const currentYear = new Date().getFullYear();
  const { data, error } = await supabase.rpc('increment_estimate_number', { p_tenant_id: tenantId });
  // The SECURITY DEFINER RPC is the ONLY safe path — the old direct-update
  // fallback silently no-ops under the owner-only company_settings RLS, which
  // would hand back a DUPLICATE number. Fail loudly rather than fabricate one.
  if (error) throw error;
  if (data?.number == null) throw new Error('increment_estimate_number returned no number');
  return { number: data.number, year: data.year || currentYear };
};

export { formatEstimateNumber } from '../utils/estimate/formatEstimateNumber.js';

export const createEstimate = async (estimateData, tenantId) => {
  if (!tenantId) return { data: null, error: new Error('tenantId is required') };
  // Include the customers join so callers that optimistically merge the
  // returned row into local state preserve the `customers` field. Without
  // this, list views fall back to "Unknown" after the first mutation.
  const { data, error } = await supabase
    .from('estimates')
    .insert({ ...estimateData, tenant_id: tenantId })
    .select('*, customers (full_name, email, cc_emails, comm_opt_out, service_address)')
    .single();
  if (!error && data?.id) {
    void writeAuditEvent({
      tenantId,
      entityType: AUDIT_ENTITY_TYPES.ESTIMATE,
      entityId: data.id,
      eventType: AUDIT_EVENT_TYPES.ESTIMATE_CREATED,
      payload: { status: estimateData?.status || 'draft' },
    });
  }
  return { data, error };
};

/**
 * NEVER put `fully_paid_at` in `estimateData`. It is derived server-side by
 * recompute_estimate_fully_paid() and a direct client write is REJECTED by the
 * estimates_guard_fully_paid_at_direct_write trigger (42501) — which would fail
 * the whole save, not just that field. Callers here pass narrow, explicit
 * payloads (`{ status }`, `{ archived_at }`, the decline fields); do not switch
 * any of them to spreading a whole estimate row back, or a stale `fully_paid_at`
 * read from local state would trip the guard and break estimate editing.
 *
 * `options` is AUDIT-ONLY and is never merged into the DB update — that
 * separation is the point: `restoreEstimate` needs the estimate's status in the
 * audit row but must NOT write `status` back to the column (doing so would also
 * flip pickEstimateEditEventType onto a status-transition event and mislabel the
 * restore).
 *
 * @param {string} estimateId
 * @param {object} estimateData columns to write
 * @param {string} tenantId
 * @param {object} [options]
 * @param {string} [options.statusAtRestore] audit-only; see restoreEstimate.
 * @param {boolean} [options.suppressAudit=false] skip the audit write entirely.
 *
 * THE suppressAudit RULE — read before passing it:
 * pickEstimateEditEventType maps the COLUMNS to a generic event, and it cannot
 * see WHY the caller wrote them. Two callers write a status this way for a
 * reason the generic event actively MISSTATES: reverting to Sent is not
 * `estimate:sent` ("we emailed it"), and repairing an orphaned invoiced estimate
 * is not `estimate:accepted` ("a human accepted it"). An audit row asserting
 * something that did not happen is worse than a duplicate, so those callers
 * suppress this write and emit their own precise event instead.
 *
 * Suppress ONLY when the caller emits a MORE SPECIFIC event for the SAME action,
 * and name that event in a comment at the call site. Never suppress without a
 * replacement — that silently deletes the action from the Activity Log with no
 * error anywhere. Default is FALSE, so every ordinary caller still audits.
 */
export const updateEstimate = async (estimateId, estimateData, tenantId, options = {}) => {
  if (!tenantId) return { data: null, error: new Error('tenantId is required') };
  // Include the customers join — fixes the "Unknown" customer bug seen
  // after archive / status-sync (estimate-derived invoice void → estimate
  // reverts to accepted) where callers optimistically replace the local
  // estimate row with this return value. The bare row drops `customers`,
  // so the list-view group header rendered "Unknown".
  const { data, error } = await supabase
    .from('estimates')
    .update({ ...estimateData, updated_at: new Date().toISOString() })
    .eq('id', estimateId)
    .eq('tenant_id', tenantId)
    .select('*, customers (full_name, email, cc_emails, comm_opt_out, service_address)')
    .single();
  if (!error && data?.id && !options.suppressAudit) {
    const eventType = pickEstimateEditEventType(estimateData);
    void writeAuditEvent({
      tenantId,
      entityType: AUDIT_ENTITY_TYPES.ESTIMATE,
      entityId: data.id,
      eventType,
      payload: buildEstimateEditAuditPayload(eventType, estimateData, options),
    });
  }
  return { data, error };
};

/**
 * External mark-as-sent — draft→sent without emailing (WhatsApp / print / hand-off).
 * Audit is server-side (delivery_method: external) — do not client-emit estimate:sent.
 */
export const markEstimateSentExternal = async (estimateId, tenantId = null) => {
  if (!tenantId) return { data: null, error: new Error('tenantId is required') };
  const { data, error } = await supabase.rpc('mark_estimate_sent_external', {
    p_estimate_id: estimateId,
  });
  return { data, error };
};

/**
 * Revert sent→draft when sent_count = 0 (undo external mark). Audit server-side.
 */
export const revertEstimateToDraft = async (estimateId, tenantId = null) => {
  if (!tenantId) return { data: null, error: new Error('tenantId is required') };
  const { data, error } = await supabase.rpc('revert_estimate_to_draft', {
    p_estimate_id: estimateId,
  });
  return { data, error };
};

/** Soft-delete: sets archived_at (RLS: billing can update; only admin could hard-delete — unused). */
export const archiveEstimate = async (estimateId, tenantId) => {
  if (!tenantId) return { data: null, error: new Error('tenantId is required') };
  return updateEstimate(estimateId, { archived_at: new Date().toISOString() }, tenantId);
};

/**
 * Clear archived_at — tenant admins only (billing-only blocked by DB trigger).
 * Emits the SINGLE `estimate:restored` audit row for a restore; callers must NOT
 * write their own (see ESTIMATE_RESTORED_EVENT_TYPE above — including its note
 * that this remains a client-side write, not a server-durable one).
 *
 * @param {string} estimateId
 * @param {string} tenantId
 * @param {string} [statusAtRestore] the estimate's status as the caller sees it,
 *   recorded in the audit payload. Audit-only — never written to the column.
 */
export const restoreEstimate = async (estimateId, tenantId, statusAtRestore) => {
  if (!tenantId) return { data: null, error: new Error('tenantId is required') };
  return updateEstimate(estimateId, { archived_at: null }, tenantId, { statusAtRestore });
};

/**
 * Hard-delete an estimate (CASCADE estimate_items; invoices.source_estimate_id SET NULL).
 * RLS: tenant admins only. The server-side guard_estimate_delete_invoice_link
 * trigger (20260924180000) can REFUSE the DELETE with ESTIMATE_HAS_INVOICE
 * when a live (non-voided, non-deleted) invoice still references this
 * estimate — so the audit event is written ONLY AFTER a CONFIRMED delete
 * (`.select('id')` proves at least one row was actually removed: RLS silently
 * deletes zero rows rather than erroring, which is also a refusal). Writing
 * the audit row before the delete used to leave a false "deleted" trail on
 * any refusal.
 */
export const permanentlyDeleteEstimate = async (estimateId, tenantId) => {
  if (!tenantId) return { error: new Error('tenantId is required') };
  const { data, error } = await supabase
    .from('estimates')
    .delete()
    .eq('id', estimateId)
    .eq('tenant_id', tenantId)
    .select('id');
  if (error) return { error };
  if (!data || data.length === 0) {
    return { error: new Error('Estimate not found or could not be deleted') };
  }
  // Confirmed delete — now, and only now, write the audit trail. The FK has
  // ON DELETE SET NULL on audit_events.entity_id, but the trail row itself
  // stays. Best-effort: an audit-write failure must not turn an already-
  // successful delete into an error.
  await writeAuditEvent({
    tenantId,
    entityType: AUDIT_ENTITY_TYPES.ESTIMATE,
    entityId: estimateId,
    eventType: AUDIT_EVENT_TYPES.ESTIMATE_DELETED,
    payload: {},
  });
  // PERF-001: purge the finalized PDF in Storage (best-effort; a storage
  // failure must NOT turn a successful record delete into an error).
  try { await removeStoredDocument({ tenantId, type: 'estimate', id: estimateId }); } catch { /* non-fatal */ }
  return { error: null };
};

/** @deprecated Prefer archiveEstimate — kept for any legacy callers. */
export const deleteEstimate = archiveEstimate;

/**
 * Mark draft/sent estimates as expired when valid_until is in the past (RPC).
 * Tenant **admin** role only — billing users are not authorized.
 */
export const expireStaleEstimates = async (tenantId) => {
  if (!tenantId) return { data: null, error: new Error('tenantId is required') };
  const { data, error } = await supabase.rpc('expire_estimates_for_tenant', { p_tenant_id: tenantId });
  return { data, error };
};

/**
 * Admin-only RPC: invoiced / partially_invoiced → accepted when no invoice has source_estimate_id pointing at the estimate.
 * Billing users get Not authorized from the RPC — do not call from billing dashboard.
 */
export const repairInvoicedEstimatesMissingInvoice = async (tenantId) => {
  if (!tenantId) return { data: null, error: new Error('tenantId is required') };
  const { data, error } = await supabase.rpc('repair_invoiced_estimates_missing_invoice', {
    p_tenant_id: tenantId,
  });
  return { data, error };
};

// RD-EST-RESP-001 error-key mapper — extracted to estimate/estimateResponseLinkErrors.js
// (500-line cap, 2026-09-24). Re-exported here so existing callers importing
// it from estimateService are unaffected.
export { getRevokeResponseLinkErrorKey } from './estimate/estimateResponseLinkErrors';

/**
 * RD-EST-RESP-001 — kill every emailed customer-response link for one estimate.
 *
 * The RPC is the ONLY writer of `response_link_version`
 * (guard_estimate_response_direct_write blocks every client path), so there is
 * no raw-PostgREST alternative to keep in sync. Tenant + role + MFA are all
 * re-derived server-side FROM THE ESTIMATE ROW — no tenant param is sent, and
 * none would be trusted.
 *
 * Returns the RPC's own jsonb: `{ estimate_id, response_link_version,
 * prior_link_version, already_responded }`. `response_link_version` is the
 * caller's optimistic patch value; `already_responded` is the CANONICAL
 * standing-response flag computed inside the same transaction, so the toast can
 * say "the customer's answer stays on file" without re-deriving it.
 *
 * No audit write here — the RPC emits `estimate:link_revoked` server-side with
 * the actor auto-filled by populate_audit_event_actor. A client-side mirror
 * would double-log.
 */
export const revokeEstimateResponseLink = async (estimateId) => {
  if (!estimateId) return { data: null, error: new Error('estimateId is required') };
  const { data, error } = await supabase.rpc('revoke_estimate_response_link', {
    p_estimate_id: estimateId,
  });
  return { data: data ?? null, error: error ?? null };
};

// estimate_items CRUD — extracted to estimate/estimateItemsService.js (500-line
// cap, 2026-09-24). Re-exported here so every existing
// `from '../../services/estimateService'` import keeps working unchanged.
export { getEstimateItems, saveEstimateItems } from './estimate/estimateItemsService';

export const createEstimateWithItems = async (estimateData, items, tenantId, opts = {}) => {
  if (!tenantId) return { data: null, error: new Error('tenantId is required') };
  const { sourceEstimateId } = opts;
  const { data, error } = await supabase.rpc('rpc_create_estimate', {
    p_estimate: { ...estimateData, tenant_id: tenantId },
    p_items: items || [],
  });
  if (error) return { data: null, error };
  // RPC returns the new estimate row (or its id). Be defensive about shape —
  // some RPCs return arrays, others single objects. Audit write is fail-soft
  // so a missing id just skips the event.
  const row = Array.isArray(data) ? data[0] : data;
  const newId = row?.id || row?.estimate_id;
  if (newId) {
    if (sourceEstimateId) {
      // Clone flow: emit ESTIMATE_CLONED (not ESTIMATE_CREATED) with source ref.
      void writeAuditEvent({
        tenantId,
        entityType: AUDIT_ENTITY_TYPES.ESTIMATE,
        entityId: newId,
        eventType: AUDIT_EVENT_TYPES.ESTIMATE_CLONED,
        payload: { source_estimate_id: sourceEstimateId, item_count: (items || []).length, status: 'draft' },
      });
    } else {
      void writeAuditEvent({
        tenantId,
        entityType: AUDIT_ENTITY_TYPES.ESTIMATE,
        entityId: newId,
        eventType: AUDIT_EVENT_TYPES.ESTIMATE_CREATED,
        payload: { status: estimateData?.status || 'draft', item_count: (items || []).length },
      });
    }
  }
  return { data, error: null };
};

/**
 * Builders-modern (2026-09-27, client wiring only — no migration change):
 * the estimate builder's finish footer needs the INSERTED row back so
 * "Enviar" can open SendEstimateModal without a refetch. `rpc_create_estimate`
 * only `RETURN`s `jsonb_build_object('id', v_id)` — no other column — so the
 * full row is composed here from the exact tenant-scoped payload the caller
 * already sent to `createEstimateWithItems` (never a client-supplied tenant:
 * `payload.tenant_id` is the session-derived `tenantId` argument) plus the id
 * the RPC handed back.
 *
 * @param {object} payload - the object passed as `estimateData` to
 *   `createEstimateWithItems` (i.e. `{ ...estimate, tenant_id, estimate_number, estimate_number_year, ... }`)
 * @param {*} data - `createEstimateWithItems`'s resolved `{ data }` (the raw
 *   RPC return — `{ id }`, or an array wrapping it)
 * @param {string} tenantId
 * @returns {object|true} the composed row (truthy, carries `.id`) when the
 *   RPC returned an id; otherwise plain `true` (defensive fallback — every
 *   existing `if (ok)` truthy check keeps working either way, but this
 *   shouldn't happen on a non-error response).
 */
export function buildCreatedEstimateRow(payload, data, tenantId) {
  const row = Array.isArray(data) ? data[0] : data;
  const newId = row?.id || row?.estimate_id;
  return newId ? { ...payload, id: newId, tenant_id: tenantId } : true;
}

/**
 * Extend an expired estimate's valid_until date and revert status to 'sent'.
 * Audit event (ESTIMATE_EXPIRATION_EXTENDED) is written server-side by the RPC —
 * do NOT call writeAuditEvent here to avoid duplicates.
 *
 * @param {string} estimateId
 * @param {string} newValidUntil - ISO date string 'YYYY-MM-DD'
 * @returns {{ data: object|null, error: Error|null }}
 */
export const extendEstimateExpiration = async (estimateId, newValidUntil) => {
  if (!estimateId) return { data: null, error: new Error('estimateId is required') };
  if (!newValidUntil) return { data: null, error: new Error('newValidUntil is required') };
  const { data, error } = await supabase.rpc('rpc_extend_estimate_expiration', {
    p_estimate_id: estimateId,
    p_new_valid_until: newValidUntil,
  });
  return { data: data ?? null, error: error ?? null };
};

export const updateEstimateWithItems = async (estimateId, estimateData, items, tenantId) => {
  if (!tenantId) return { data: null, error: new Error('tenantId is required') };
  const { data, error } = await supabase.rpc('rpc_update_estimate', {
    p_estimate_id: estimateId,
    p_estimate: estimateData,
    p_items: items || [],
    p_tenant_id: tenantId,
  });
  if (error) return { data: null, error };
  const eventType = pickEstimateEditEventType(estimateData);
  void writeAuditEvent({
    tenantId,
    entityType: AUDIT_ENTITY_TYPES.ESTIMATE,
    entityId: estimateId,
    eventType,
    payload:
      eventType === AUDIT_EVENT_TYPES.ESTIMATE_EDITED
        ? { changed_fields: Object.keys(estimateData || {}), item_count: (items || []).length }
        : { to_status: estimateData?.status || null, item_count: (items || []).length },
  });
  return { data, error: null };
};
